import React from 'react';
import BillingAddressForm from './BillingAddressForm';

const ShippingAddressForm = ({
    shippingAddress,
    setShippingAddress,
    email,
    setEmail,
    billingAddress,
    setBillingAddress,
    useSameAsShipping,
    setUseSameAsShipping,
    errors,
    loading,
    handleAddressSubmit,
    usStates,
    countries = [],
    // New props for address selection
    isLoggedIn = false,
    customerAddresses = [],
    showAddressSelector = false,
    isEditingAddress = false,
    selectedAddressId = null,
    onAddressSelect = () => {},
    onEditAddress = () => {},
    onNewAddress = () => {}
}) => {
    return (
        <div className="form-section">
            <h2 className="form-title">Shipping Address</h2>

            {/* Error Display */}
            {errors.general && (
                <div className="error-message">
                    {errors.general}
                </div>
            )}

            {/* Address Selector for logged-in customers with multiple addresses */}
            {isLoggedIn && showAddressSelector && !isEditingAddress && (
                <div className="address-selector" style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', fontWeight: '600' }}>
                        Select Shipping Address
                    </h3>
                    <div className="saved-addresses-grid" style={{
                        display: 'grid',
                        gap: '1rem',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        marginBottom: '1rem'
                    }}>
                        {customerAddresses.map((address) => (
                            <div
                                key={address.id}
                                className={`address-card ${selectedAddressId === address.id ? 'selected' : ''}`}
                                onClick={() => onAddressSelect(address)}
                                style={{
                                    border: selectedAddressId === address.id ? '2px solid #007bff' : '1px solid #ddd',
                                    borderRadius: '8px',
                                    padding: '1rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    backgroundColor: selectedAddressId === address.id ? '#f0f8ff' : 'white'
                                }}
                            >
                                <div style={{ marginBottom: '0.5rem' }}>
                                    <strong>{address.firstname} {address.lastname}</strong>
                                    {address.default_shipping && (
                                        <span style={{
                                            marginLeft: '0.5rem',
                                            fontSize: '0.75rem',
                                            backgroundColor: '#28a745',
                                            color: 'white',
                                            padding: '0.125rem 0.5rem',
                                            borderRadius: '4px'
                                        }}>
                                            Default
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                    <div>{address.street?.join(', ')}</div>
                                    <div>{address.city}, {address.region?.region || ''} {address.postcode}</div>
                                    <div>{address.country_code}</div>
                                    {address.telephone && <div>Tel: {address.telephone}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            type="button"
                            onClick={onEditAddress}
                            className="btn-secondary"
                            style={{ padding: '0.5rem 1rem' }}
                        >
                            Edit Address
                        </button>
                    </div>
                </div>
            )}

            {/* Show form only for guests or when editing/creating new address */}
            {(!isLoggedIn || !showAddressSelector || isEditingAddress) && (
            <form onSubmit={handleAddressSubmit} className="form-grid">
                <div className="form-group">
                    <label className="form-label">
                        Email Address *
                    </label>
                    <input
                        type="email"
                        placeholder="Enter email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className={`form-input ${errors.email ? 'error' : ''}`}
                    />
                    {errors.email && (
                        <div className="form-error">
                            {errors.email}
                        </div>
                    )}
                </div>

                <div className="form-row-2">
                    <div className="form-group">
                        <label className="form-label">
                            First Name *
                        </label>
                        <input
                            type="text"
                            placeholder="Enter first name"
                            value={shippingAddress.firstname}
                            onChange={(e) => setShippingAddress(prev => ({ ...prev, firstname: e.target.value }))}
                            required
                            className={`form-input ${errors.firstname ? 'error' : ''}`}
                        />
                        {errors.firstname && (
                            <div className="form-error">
                                {errors.firstname}
                            </div>
                        )}
                    </div>
                    <div className="form-group">
                        <label className="form-label">
                            Last Name *
                        </label>
                        <input
                            type="text"
                            placeholder="Enter last name"
                            value={shippingAddress.lastname}
                            onChange={(e) => setShippingAddress(prev => ({ ...prev, lastname: e.target.value }))}
                            required
                            className={`form-input ${errors.lastname ? 'error' : ''}`}
                        />
                        {errors.lastname && (
                            <div className="form-error">
                                {errors.lastname}
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-group">
                    <label className="form-label">
                        Street Address *
                    </label>
                    <input
                        type="text"
                        placeholder="Enter street address"
                        value={shippingAddress.street[0]}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, street: [e.target.value] }))}
                        required
                        className={`form-input ${errors.street ? 'error' : ''}`}
                    />
                    {errors.street && (
                        <div className="form-error">
                            {errors.street}
                        </div>
                    )}
                </div>

                <div className="form-group">
                    <label className="form-label">
                        Country *
                    </label>
                    <select
                        value={shippingAddress.country_code || ''}
                        onChange={(e) => {
                            const selectedCountry = countries.find(c => c.two_letter_abbreviation === e.target.value);
                            setShippingAddress(prev => ({
                                ...prev,
                                country_code: e.target.value,
                                region_id: '',
                                region: ''
                            }));
                        }}
                        required
                        className={`form-select ${errors.country ? 'error' : ''}`}
                    >
                        <option value="">Select Country</option>
                        {countries.map((country) => (
                            <option key={country.id} value={country.two_letter_abbreviation}>
                                {country.full_name_locale}
                            </option>
                        ))}
                    </select>
                    {errors.country && (
                        <div className="form-error">
                            {errors.country}
                        </div>
                    )}
                </div>

                <div className="form-row-3">
                    <div className="form-group">
                        <label className="form-label">
                            City *
                        </label>
                        <input
                            type="text"
                            placeholder="Enter city"
                            value={shippingAddress.city}
                            onChange={(e) => setShippingAddress(prev => ({ ...prev, city: e.target.value }))}
                            required
                            className={`form-input ${errors.city ? 'error' : ''}`}
                        />
                        {errors.city && (
                            <div className="form-error">
                                {errors.city}
                            </div>
                        )}
                    </div>
                    <div className="form-group">
                        <label className="form-label">
                            State *
                        </label>
                        <select
                            value={shippingAddress.region_id || ''}
                            onChange={(e) => {
                                const selectedOption = e.target.options[e.target.selectedIndex];
                                setShippingAddress(prev => ({
                                    ...prev,
                                    region_id: parseInt(e.target.value),
                                    region: selectedOption.text
                                }));
                            }}
                            required
                            className={`form-select ${errors.region ? 'error' : ''}`}
                        >
                            <option value="">Select State</option>
                            {usStates.map((state) => (
                                <option key={state.id} value={state.id}>
                                    {state.name}
                                </option>
                            ))}
                        </select>
                        {errors.region && (
                            <div className="form-error">
                                {errors.region}
                            </div>
                        )}
                    </div>
                    <div className="form-group">
                        <label className="form-label">
                            ZIP Code *
                        </label>
                        <input
                            type="text"
                            placeholder="Enter ZIP code"
                            value={shippingAddress.postcode}
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^\d-]/g, '');
                                setShippingAddress(prev => ({ ...prev, postcode: value }));
                            }}
                            required
                            className={`form-input ${errors.postcode ? 'error' : ''}`}
                        />
                        {errors.postcode && (
                            <div className="form-error">
                                {errors.postcode}
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-group">
                    <label className="form-label">
                        Phone Number *
                    </label>
                    <input
                        type="tel"
                        placeholder="Enter phone number"
                        value={shippingAddress.telephone}
                        onChange={(e) => {
                            const value = e.target.value.replace(/[^\d]/g, '');
                            setShippingAddress(prev => ({ ...prev, telephone: value }));
                        }}
                        required
                        className={`form-input ${errors.telephone ? 'error' : ''}`}
                    />
                    {errors.telephone && (
                        <div className="form-error">
                            {errors.telephone}
                        </div>
                    )}
                </div>

                <BillingAddressForm
                    billingAddress={billingAddress}
                    setBillingAddress={setBillingAddress}
                    useSameAsShipping={useSameAsShipping}
                    setUseSameAsShipping={setUseSameAsShipping}
                    errors={errors}
                    usStates={usStates}
                />

                <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                >
                    {loading && (
                        <div className="loading-spinner" />
                    )}
                    {loading ? 'Processing...' : 'Continue to Shipping'}
                </button>
            </form>
            )}

            {/* Continue button when address is selected but not editing */}
            {isLoggedIn && showAddressSelector && !isEditingAddress && selectedAddressId && (
                <button
                    type="button"
                    onClick={handleAddressSubmit}
                    disabled={loading}
                    className="btn-primary"
                    style={{ marginTop: '1rem' }}
                >
                    {loading && (
                        <div className="loading-spinner" />
                    )}
                    {loading ? 'Processing...' : 'Continue to Shipping'}
                </button>
            )}
        </div>
    );
};

export default ShippingAddressForm;